// Location QRs for a picking job: cards to fix to a block's end post or a
// row's, which a picker scans in Dataphyll Crew to say where they are
// working instead of choosing it from the list. A row's card sets the job
// and the row; the job's card sets the job alone.
//
// The squares are `DL~<id>` (docs/crew-qr-v2.md), built by the API (GET
// /api/picking-jobs/:id/location-qrs) with the encoder the clock-on card uses.
// The PDF is written by hand the way the clock-on card is (teams/qrCard.ts):
// four cards to an A4 page.

import { pdfDocument, pdfText, rgb, wrap } from '../teams/qrCard'

export const LOCATION_SCAN_NOTE = 'Scan in the Dataphyll Crew app once you are clocked on, to set your job and location. It does not clock you on.'

export interface LocationQrCard {
  id: string
  kind: 'job' | 'row'
  // "Block 1 / Tunnel 2 / Row 3"; null on the job's own card.
  path: string | null
  location_id: string | null
  text: string
  modules: string[]
}

export interface LocationQrs {
  job: { id: string; name: string; site_name: string; exact_variety: string }
  cards: LocationQrCard[]
}

const W = 595
const H = 842
const COLS = 2
const ROWS = 2
const CARD_W = 255
const CARD_H = 375
const GAP_X = W - 2 * 30 - COLS * CARD_W
const GAP_Y = 22

function drawCard(ops: string[], q: LocationQrs, card: LocationQrCard, left: number, top: number, printedOn: string) {
  const text = (s: string, x: number, y: number, size: number, bold = false, colour = '#201e1d') =>
    ops.push(`BT /${bold ? 'F2' : 'F1'} ${size} Tf ${rgb(colour)} rg ${x.toFixed(1)} ${y.toFixed(1)} Td ${pdfText(s)} Tj ET`)
  const rect = (x: number, y: number, w: number, h: number, colour: string) =>
    ops.push(`${rgb(colour)} rg ${x.toFixed(1)} ${y.toFixed(1)} ${w.toFixed(1)} ${h.toFixed(1)} re f`)
  // Bold Helvetica runs about 0.55 of its size per character.
  const centred = (s: string, y: number, size: number, bold = false, colour?: string) =>
    text(s, left + Math.max(12, (CARD_W - s.length * size * (bold ? 0.55 : 0.5)) / 2), y, size, bold, colour)

  // Slate, never a clock-on card's terracotta or sage: a location card must
  // not be taken for one, and says so in its header.
  const headH = 46
  rect(left, top - headH, CARD_W, headH, '#34506b')
  text('LOCATION QR', left + 16, top - 22, 13, true, '#ffffff')
  text('Not a clock-on card', left + 16, top - 36, 7.5, false, '#d3dde7')

  // The job, its site and its variety, each named, so the card is checked
  // against the block it is fixed to before anyone scans it.
  let y = top - headH - 16
  for (const [label, value] of [
    ['Job', q.job.name],
    ['Site', q.job.site_name],
    ['Variety', q.job.exact_variety || 'No variety'],
  ]) {
    text(label, left + 16, y, 7.5, false, '#857c6d')
    text(wrap(value, 9.5, CARD_W - 80)[0], left + 62, y, 9.5, true)
    y -= 13
  }

  // About 5.3 cm across: modules of a millimetre or more at any version.
  const qrSide = 150
  const n = card.modules.length
  const m = n ? qrSide / n : 0
  const qx = left + (CARD_W - qrSide) / 2
  const qyTop = y - 4
  card.modules.forEach((row, r) => {
    for (const run of row.matchAll(/1+/g)) {
      ops.push(`0.125 0.118 0.114 rg ${(qx + run.index! * m).toFixed(2)} ${(qyTop - (r + 1) * m).toFixed(2)} ${(run[0].length * m).toFixed(2)} ${m.toFixed(2)} re f`)
    }
  })

  // A row's card says where it is, large enough to read from the next row;
  // the job's own card has nothing to add to its job.
  y = qyTop - qrSide - 14
  if (card.path) {
    centred('LOCATION', y, 7, true, '#857c6d')
    y -= 14
    for (const line of wrap(card.path, 12, CARD_W - 24).slice(0, 2)) {
      centred(line, y, 12, true)
      y -= 14
    }
  }
  if (card.location_id) {
    centred(card.location_id, y, 8, false, '#857c6d')
    y -= 12
  }

  const noteLines = wrap(LOCATION_SCAN_NOTE, 7.5, CARD_W - 40)
  const noteH = noteLines.length * 10 + 12
  rect(left + 12, y - noteH + 6, CARD_W - 24, noteH, '#e9eef3')
  noteLines.forEach((l, i) => text(l, left + 20, y - 6 - i * 10, 7.5, false, '#34506b'))

  const foot = top - CARD_H + 12
  text(`Printed ${printedOn} from Dataphyll CONTROL`, left + 16, foot, 6.5, false, '#a89e8c')
  ops.push(`0.886 0.855 0.796 RG 0.8 w ${left} ${(top - CARD_H).toFixed(1)} ${CARD_W} ${CARD_H} re S`)
}

// Cards as pages of four, for pdfDocument, or one to a page, centred at the
// top (`single`: the team schedule hands each job out on its own sheet). Each
// card carries its own job, so one PDF can hold several jobs' cards.
export function locationCardPages(cards: { q: LocationQrs; card: LocationQrCard }[], printedOn: string, single = false): string[] {
  const perPage = single ? 1 : COLS * ROWS
  const pages: string[] = []
  for (let i = 0; i < Math.max(1, cards.length); i += perPage) {
    const ops: string[] = []
    cards.slice(i, i + perPage).forEach(({ q, card }, k) => {
      if (single) return drawCard(ops, q, card, (W - CARD_W) / 2, H - 60, printedOn)
      const col = k % COLS
      const row = Math.floor(k / COLS)
      drawCard(ops, q, card, 30 + col * (CARD_W + GAP_X), H - 30 - row * (CARD_H + GAP_Y), printedOn)
    })
    pages.push(ops.join('\n'))
  }
  return pages
}

export const locationQrsPdf = (q: LocationQrs, printedOn: string, cards: LocationQrCard[] = q.cards): string =>
  pdfDocument(locationCardPages(cards.map((card) => ({ q, card })), printedOn))

export const locationQrsPdfName = (jobName: string) =>
  `location-qrs-${jobName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'job'}.pdf`
